import { useState } from 'react';
import { format, isPast, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
// @mui
import {
  Card,
  Box,
  Stack,
  Typography,
  Button,
  Skeleton,
} from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
import useGoals from '../../hooks/useGoals';
import useResponsive from '../../hooks/useResponsive';
// components
import SvgIconStyle from '../../components/SvgIconStyle';
import { GoalListDialog } from './';

export default function GoalCard({ crewId }) {
  const smUp = useResponsive('up', 'sm');
  const { user } = useAuth();
  const companyId = user?.employee.company?.[0]?.id;
  const { goals, totalGoals, isLoading } = useGoals(companyId, crewId);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // 미완료 목표만 필터링
  const incompleteGoals = goals.filter(goal => !goal.is_completed);

  if (isLoading) {
    return (
      <Card sx={{ height: '100%', maxHeight: 167 }}>
        <Box sx={{ px: { xs: 3, sm: 5 }, py: { xs: 3, sm: 4 } }}>
          <Skeleton variant="rectangular" height={120} />
        </Box>
      </Card>
    );
  }

  // 목표가 하나도 없는 경우
  if (totalGoals === 0) {
    return (
      <>
        <Card sx={{ height: '100%', maxHeight: 167, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ 
            px: { xs: 3, sm: 5 }, 
            py: { xs: 3, sm: 4 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Stack spacing={2} sx={{ height: '100%' }}>
              <Typography variant="h6">
                회사의 요청사항이 없습니다!
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                크루에게 주요 요청사항을 전달해주세요. 크루가 등록한 내용이 여기에 표시됩니다.
              </Typography>
            </Stack>
          </Box>
        </Card>
      </>
    );
  }

  // 목표가 있는 경우
  return (
    <>
      <Card sx={{ height: '100%', maxHeight: 167, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ 
          px: { xs: 3, sm: 5 }, 
          pt: { xs: 3, sm: 4 },
          pb: 2,
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              목표일정
            </Typography>
            <Button
              variant="text"
              size="small"
              sx={{ color: "text.secondary" }}
              onClick={() => setIsEditDialogOpen(true)}
              startIcon={<SvgIconStyle src={"/icons/ic_search.svg"} sx={{ width: 16, height: 16 }} />}
              disableRipple
            >
              {totalGoals}건 자세히보기
            </Button>
          </Stack>
        </Box>
        {incompleteGoals.length === 0 ? (
          <Box sx={{ 
            px: { xs: 3, sm: 5 }, 
            pb: { xs: 3, sm: 4 },
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              진행 중인 목표가 없습니다.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            mb: { xs: 3, sm: 4 },
            px: 2,
          }}>
            <Box sx={{
              px: { xs: 1, sm: 3 }, 
            }}>
              <Stack spacing={1.5}>
                {incompleteGoals.map((goal) => {
                  const isOverdue = goal.due_date && isPast(parseISO(goal.due_date));
                  
                  return (
                    <Stack
                      key={goal.id}
                      direction="row"
                      alignItems="flex-start"
                      spacing={1.5}
                    >
                      <SvgIconStyle
                        src="/icons/ic_calendar.svg"
                        sx={{
                          width: 20,
                          height: 20,
                          color: isOverdue ? 'error.main' : 'text.secondary',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          color: isOverdue ? 'error.main' : 'text.primary',
                        }}
                      >
                        {goal.content}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isOverdue ? 'error.main' : 'text.secondary',
                        }}
                      >
                        {goal.due_date
                          ? format(parseISO(goal.due_date), 'MM월 dd일', { locale: ko })
                          : '기한 없음'}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        )}
      </Card>
      
      <GoalListDialog
        isOpen={isEditDialogOpen}
        closeDialog={() => setIsEditDialogOpen(false)}
        crewId={crewId}
        companyId={companyId}
      />
    </>
  );
}